import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { omit, pick } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../shared/permissions';
import {
    CreateWebPageDocument,
    type CreateWebPageMutation,
    type CreateWebPageMutationVariables,
} from '../../../api/mutations/createWebPage';
import { type CustomWebPageBlockInput, WebPageBlockType, type TextCarouselWebPageBlockInput } from '../../../api/types';
import useLoadingButton from '../../../components/button/useLoadingButton';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useHandleError from '../../../utilities/useHandleError';
import {
    type ColumnBlockMainInput,
    type ImageBlockInput,
    type TextImageBlockInput,
    type WebpageFormikValue,
} from '../WebpageDetailsPage/shared';
import useHandleWebPageAsset from '../WebpageDetailsPage/useHandleWebPageAsset';
import useWebpageValidator from '../WebpageDetailsPage/useValidator';
import WebpageForm from '../WebpageDetailsPage/WebpageForm';

const initialValues: WebpageFormikValue = {
    webPageBlock: [],
    bannerTitle: { defaultValue: '', overrides: [] },
    bannerSubTitle: { defaultValue: '', overrides: [] },
    pageTitle: { defaultValue: '', overrides: [] },
    isActive: false,
    urlSlug: '',
};

const AddWebpagePage = () => {
    const { t } = useTranslation('webpageDetails');
    const navigate = useNavigate();
    const { permissions: accountPermissions } = useAccount();
    const { setLoading, LoadingButton } = useLoadingButton();
    const apolloClient = useApolloClient();

    const useWebPageAsset = useHandleWebPageAsset();

    const validate = useWebpageValidator(false);

    const onSubmit = useHandleError<WebpageFormikValue>(
        async value => {
            const { webPageBlock, companyId, bannerImage, mobileBannerImage, moduleId, ...webPageInput } = value;
            setLoading(true);
            // submitting message
            message.loading({
                content: t('webpageDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const { data } = await apolloClient.mutate<CreateWebPageMutation, CreateWebPageMutationVariables>({
                mutation: CreateWebPageDocument,
                variables: {
                    moduleId,
                    webPageInput,
                    webPageBlockInput: (webPageBlock || [])
                        .map(block => {
                            switch (block.type) {
                                case WebPageBlockType.Column: {
                                    const columnBlockInput = block as ColumnBlockMainInput;

                                    const columnBlock = {
                                        ...pick(['type', 'columns'], columnBlockInput),
                                    };

                                    columnBlock.columns = columnBlockInput.columns.map(column => ({
                                        ...omit(['image'], column),
                                    }));

                                    return { columnBlockInput: columnBlock };
                                }

                                case WebPageBlockType.Custom: {
                                    const customBlockInput = block as CustomWebPageBlockInput;
                                    const customBlock = { ...omit(['id'], customBlockInput) };

                                    return { customBlockInput: customBlock };
                                }

                                case WebPageBlockType.Image: {
                                    const imageBlockInput = block as ImageBlockInput;
                                    const imageBlock = {
                                        ...pick(['descriptions', 'type', 'title', 'button'], imageBlockInput),
                                    };
                                    imageBlock.descriptions = imageBlock.descriptions.map(description => ({
                                        ...omit(['icon'], description),
                                    }));

                                    return { imageBlockInput: imageBlock };
                                }

                                case WebPageBlockType.TextImage: {
                                    const textImageBlockInput = block as TextImageBlockInput;
                                    const typeBlock = { ...omit(['image'], textImageBlockInput) };

                                    return { textImageBlockInput: typeBlock };
                                }

                                case WebPageBlockType.TextCarousel: {
                                    const textCarouselBlockInput = block as TextCarouselWebPageBlockInput;

                                    return {
                                        textCarouselBlockInput: {
                                            ...pick(['id', 'type', 'title', 'descriptions'], textCarouselBlockInput),
                                        },
                                    };
                                }

                                default:
                                    throw new Error('WebPageBlockType not support');
                            }
                        })
                        .filter(Boolean),
                },
            });

            await useWebPageAsset(value, data.webpage.id, data.webpage);

            // inform about success
            message.success({
                content: t('webpageDetails:messages.createSuccessful'),
                key: 'primary',
            });
            navigate('/admin/webpages');
        },
        [apolloClient, navigate, setLoading, t, useWebPageAsset],
        undefined,
        () => {
            setLoading(false);
            message.destroy('primary');
        }
    );

    const hasCreatePermission = hasPermissions(accountPermissions, [permissionKind.updateWebpage]);

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="addWebPageForm" htmlType="submit" type="primary">
                    {t('webpageDetails:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/webpages')}
            title={t('webpageDetails:title')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="addWebPageForm" name="addWebPageForm" onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <WebpageForm permissions={{ hasCreatePermission }} />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddWebpagePage;
